import { PageviewData, useUmami } from '@danielgtmn/umami-react';
import { useEventCallback } from '@mui/material';
import { FeatureFlags } from 'application/constants/env-variables';
import { RouterState } from 'react-router';

type EventData = {
  category: string;
  action: string;
  name?: string;
  value?: number;
  customData?: Record<string, any>;
} & PageviewData &
  Record<string, any>;

export function useAnalytics() {
  const { trackPageview: trackPageviewUmami } = useUmami();

  const trackEvent = useEventCallback((data: EventData) => {
    const umamiData = processUmamiEventData(data);
    trackPageviewUmami(umamiData);
  });

  return { trackEvent };
}

/*
 * function that could be used if you can't use hooks for some reason.
 * Copied from https://github.com/danielgtmn/react-umami/blob/main/src/index.tsx
 */
export function trackUmami(pageviewData?: EventData) {
  if (typeof window !== 'undefined' && (window as any).umami) {
    if (typeof (window as any).umami.track === 'function') {
      if (pageviewData && Object.keys(pageviewData).length > 0) {
        // For pageviews with custom data, call track with the data
        const data = {
          ...processUmamiEventData(pageviewData),
          url:
            pageviewData.url ||
            window.location.pathname + window.location.search,
          title: pageviewData.title || document.title,
          referrer: pageviewData.referrer || document.referrer,
        };
        (window as any).umami.track(data);
      } else {
        // For default pageviews, call track without arguments (defaults to current page)
        (window as any).umami.track();
      }
    }
  }
}
function processUmamiEventData(data: EventData) {
  const { name, category, action, customData, ...rest } = data;
  const additionalData: Record<string, any> = {};
  if (name) {
    additionalData['name'] = name;
  }

  return {
    name: category + ' - ' + action,
    website: FeatureFlags.umamiSiteId(),
    data:
      Object.keys(additionalData).length > 0
        ? { ...additionalData, ...customData }
        : customData,
    ...rest,
  };
}

export function sendRouteChangeEvent(
  state: RouterState,
  customProps: Record<string, any> = {},
) {
  const match = state.matches[state.matches.length - 1];
  const route = match?.route;

  if (route) {
    trackUmami({
      category: 'page',
      action: 'view',
      referrer: route.path,
      customData: {
        ...(match.params as any),
        ...customProps,
      },
    });
  } else {
    trackUmami({
      category: 'page',
      action: 'view',
      referrer: state.location.pathname,
      customData: {
        ...(match.params as any),
        ...customProps,
      },
    });
  }
}
